/**
 * Lightweight phone formatting for display while typing.
 *
 * Goal: improve readability in forms (Event Calendar registration).
 * Input is treated as freeform; we preserve only digits and format as US-style:
 *  - ###-###-####
 * Extra digits are appended after a space.
 */

function digitsOnly(value) {
  return String(value == null ? "" : value).replace(/\D/g, "");
}

/**
 * Normalize a phone-like value to digits only.
 * Useful for tel: links and API payload normalization when desired.
 *
 * @param {string} value
 * @returns {string}
 */
export function normalizePhoneDigits(value) {
  return digitsOnly(value);
}

/**
 * Format US-style phone as user types.
 * Examples:
 *  - "9" -> "9"
 *  - "9723" -> "972-3"
 *  - "9723758668" -> "972-375-8668"
 *
 * @param {string} value
 * @returns {string}
 */
export function formatUsPhoneAsYouType(value) {
  let d = digitsOnly(value);
  if (!d) return "";

  // Autofill commonly includes "+1" for US numbers. If we have a leading US
  // country code and enough digits, strip it so we still format as 10-digit US.
  if (d.length >= 11 && d[0] === "1") {
    d = d.slice(1);
  }

  const area = d.slice(0, 3);
  const mid = d.slice(3, 6);
  const last = d.slice(6, 10);
  const extra = d.slice(10);

  if (d.length <= 3) return `${area}`;
  if (d.length <= 6) return `${area}-${mid}`;

  const base = `${area}-${mid}-${last}`;
  return extra ? `${base} ${extra}` : base;
}

/**
 * Format a phone for display. Returns '—' when empty.
 * Currently uses the same behavior as "as you type" formatting, but callers
 * should prefer this for display so behavior can be adjusted centrally later.
 *
 * @param {string} value
 * @returns {string}
 */
export function formatPhoneDisplay(value) {
  const s = (value != null ? String(value) : "").trim();
  if (!s) return "—";
  return formatUsPhoneAsYouType(s);
}

